import {
  DeskFormParams,
  DropdownOption,
  FormFieldState,
  MeetingFormParams,
  OfficeFormParams,
  Timeslot,
  formatCurrency,
  bookingPricing,
} from '@studio/atomic-ui'
import { TmPdpProps } from './index'
import { DateRange } from 'react-day-picker'
import { format, setHours, setMinutes, setSeconds } from 'date-fns'
import { checkAvailability, getCheckDayPassAvailability } from '../../services/bff/availability'
import { getHoursFromDuration, getHoursFromTimeslot, transformTime } from '../../utils/get-hours'
import { ProductSource } from '../../types/product'
import { ProductType } from '../../types/product'
import { defaultBookableDays } from '../../env'
import { BookingSource } from '../../utils/booking-source'

export const officeFormConfig = (props: TmPdpProps): Omit<OfficeFormParams, 'reservation' | 'total'> => {
  return {
    rate: `${formatCurrency(props.prices?.unitPrice ?? 0)}/day`,
    discounts: [
      ...(props.prices?.availableDiscounts[0] ? [`${props.prices.availableDiscounts[0]}% off for 5+ days`] : []),
      ...(props.prices?.availableDiscounts[1] ? [`${props.prices.availableDiscounts[1]}% off for 20+ days`] : []),
    ],
    capacity: props.capacity && !props.hideCapacityOnPdp ? `${props.capacity}` : '',
    bookableDays: props.bookableDays ?? defaultBookableDays,
  }
}

export const deskFormConfig = (props: TmPdpProps): Omit<DeskFormParams, 'reservation' | 'total'> => {
  return {
    availableDesks: props.availables.length,
    discounts: [
      ...(props.prices?.availableDiscounts[0] ? [`${props.prices.availableDiscounts[0]}% off for 5+ days`] : []),
      ...(props.prices?.availableDiscounts[1] ? [`${props.prices.availableDiscounts[1]}% off for 20+ days`] : []),
    ],
    rate: `${formatCurrency(props.prices?.unitPrice ?? 0)}/day`,
    bookableDays: props.bookableDays ?? defaultBookableDays,
  }
}

export const meetingFormConfig = (props: TmPdpProps): Omit<MeetingFormParams, 'reservation' | 'total'> => {
  return {
    rate: `${formatCurrency(props.prices?.unitPrice ?? 0)}/hour`,
    discounts: [
      ...(props.prices?.availableDiscounts[0] ? [`${props.prices.availableDiscounts[0]}% off for 4+ hours`] : []),
      ...(props.prices?.availableDiscounts[1] ? [`${props.prices.availableDiscounts[1]}% off for 8+ hours`] : []),
    ],
    capacity: props.capacity && !props.hideCapacityOnPdp ? `${props.capacity}` : '',
    room: props.source !== BookingSource.TRIPLESEAT ? 'studio' : 'gather',
    times: [],
    bookableDays: props.bookableDays ?? defaultBookableDays,
    source: props.source,
    minimumBookingDuration: props.minimumBookingDuration?.value ?? '0.5',
  }
}

export const pricePlaceholder: ReturnType<typeof bookingPricing> = {
  total: {},
  reservation: {},
}

export const prepareDateForBackend = (
  range: DateRange,
  startTime?: Timeslot,
  duration?: DropdownOption
): Record<'from' | 'to', string> => {
  let from = (range.from as Date) ?? new Date()
  let to = range.to ?? from

  if (startTime && duration && !range.to) {
    const timeToAdd = getHoursFromTimeslot(startTime.timeslot)
    const durationToAdd = getHoursFromDuration(+duration.value, timeToAdd)

    from = setSeconds(setMinutes(setHours(from, timeToAdd.hours), timeToAdd.minutes), 0)
    to = setSeconds(setMinutes(setHours(to, durationToAdd.hours), durationToAdd.minutes), 0)
  } else {
    from = setSeconds(setMinutes(setHours(from, 0), 0), 0)
    to = setSeconds(setMinutes(setHours(to, 23), 59), 59)
  }

  return {
    from: format(from, `yyyy-MM-dd'T'HH:mm:ss`),
    to: format(to, `yyyy-MM-dd'T'HH:mm:ss`),
  }
}

export const buildBookingQuery = (id: number, source: string, state: FormFieldState) => {
  const query: Record<string, string> = {
    product: `${id}`,
    source: source,
    additionalServices: (state.additionalServices?.value as string) ?? '',
  }

  const range = state.date.value as DateRange
  const { from, to } = prepareDateForBackend(
    range,
    state.startTime?.value as Timeslot | undefined,
    state.duration?.value as DropdownOption | undefined
  )
  query.from = from
  query.to = to

  if (state.desks?.value) {
    query.quantity = (state.desks.value as DropdownOption).value
  }
  if (state.duration?.value) {
    query.duration = (state.duration.value as DropdownOption).value
    query.labelDuration = (state.duration.value as DropdownOption).label
  }
  if (state.startTime?.value) {
    query.startTime = (state.startTime.value as Timeslot).timeslot
    query.labelTime = (state.startTime.value as Timeslot).label
  }

  return query
}

export const checkDayPassAvailability = async (
  location: string,
  bookingSource: string,
  range?: DateRange
): Promise<number> => {
  if (range) {
    const from = setSeconds(setMinutes(setHours(range.from as Date, 0), 0), 0)
    const to = setSeconds(setMinutes(setHours(range.to ?? from, 23), 59), 59)
    const formattedFrom = format(from, `yyyy-MM-dd'T'HH:mm:ss`)
    const formattedTo = format(to, `yyyy-MM-dd'T'HH:mm:ss`)
    const response = await getCheckDayPassAvailability(formattedFrom, formattedTo, location, bookingSource)
    const { availableDesks } = response
    return availableDesks
  }
  return 0
}

/** Error for each of the tabs inside the time selector. Tabs come in this order: "For You", "Morning", "Afternoon"; In that order the error messages should be in this array */
const noSlotError: string[] = [
  'There are no recommended timeslots that match your criteria. Please select Morning or afternoon for availability.',
  'There are no available timeslots that match your criteria, please select the Afternoon tab.',
  'There are no available timeslots that match your criteria, please select the Morning tab',
]

export const checkSpaceAvailability = async (
  id: number,
  source: ProductSource,
  singleDay = false,
  productType: ProductType,
  range?: DateRange,
  startTime?: Timeslot,
  duration?: DropdownOption,
  building?: string
) => {
  if (!range) return

  const { from, to } = singleDay
    ? prepareDateForBackend({ from: range.from })
    : prepareDateForBackend(range, startTime, duration)

  const response = await checkAvailability({
    bookingSource: source,
    location: building !== undefined ? building : 'the-spiral',
    productId: id.toString(),
    startDate: from,
    endDate: to,
    duration: duration?.value,
    productType,
    startTime: startTime?.timeslot,
  })

  /*
    check if both start times exist, and product is available
    if not, remove start times this fix is for scenarios where
    the back-end returns start times, despite returning a false availability
  */
  if (response.startTimes && response.isAvailable) {
    response.startTimes = response.startTimes.map((list, i) => ({
      ...list,
      timeslotLabels: list.timeslots.map(transformTime),
      noSlotsError: noSlotError[i],
    }))
  } else {
    response.startTimes = []
  }

  return response
}

export const productTypeNames: Record<ProductType, string> = {
  event_space: 'Event Space',
  day_pass: 'Day Pass',
  meeting_room: 'Meeting Room',
  office: 'Office',
  suite: 'Suite',
}
